// src/pages/course/CoursePage.tsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useContentFetch from "./hooks/useContentFetch";
import log from "loglevel";
import { Layout, Menu, MenuProps, message, Spin } from "antd";
import styles from "./CoursePage.module.css";
import { ContentWithTextPlayer } from "./ContentWithTextPlayer";
import Sider from "antd/es/layout/Sider";
import { convertLessonsToMenuItems, getHtmlUrlByLessonId, getNextLessonId, initializeLessonIndex, clearLessonIndex } from "../../utils/utils";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { setCourseId, setLessonId, setCurrentSentence } from "../../store/slices/audioSlice";
import eventBus from "../../utils/eventBus";
import { EVENTS } from "../../config/GlobalConfig";
import { fetchLessonData } from "../../store/thunks/lessonThunks";
import SelectedKeyManager from "../../utils/SelectedKeyManager";
import { selectLessonsByCourseId } from "../../store/selectors/lessonSelectors";

export const CoursePage: React.FC = () => {
    const params = useParams<{ courseId: string; lessonId?: string }>();
    const courseId = Number(params.courseId);
    const lessonId = params.lessonId ? Number(params.lessonId) : null;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { getHtmlContent } = useContentFetch();

    const [collapsed, setCollapsed] = useState(false);
    const [contentHTML, setContentHTML] = useState<string>('');
    const [htmlUrl, setHtmlUrl] = useState<string>('');
    const [isLoadingLessons, setIsLoadingLessons] = useState(false);
    const [isLoadingContent, setIsLoadingContent] = useState(false);
    const { isLogin } = useAppSelector((state) => state.user);

    const lessons = useAppSelector(state => selectLessonsByCourseId(state, courseId));

    useEffect(() => {
        if (courseId) {
            setIsLoadingLessons(true);
            dispatch(fetchLessonData(courseId))
                .finally(() => setIsLoadingLessons(false));
        }
    }, [dispatch, courseId]);

    useEffect(() => {
        if (courseId > 0 && lessons.length > 0) {
            dispatch(setCourseId(courseId));
            initializeLessonIndex(lessons, courseId);

            if (lessonId) {
                const parsedLessonId = Number(lessonId);
                dispatch(setLessonId(parsedLessonId));
                dispatch(setCurrentSentence(0));
                SelectedKeyManager.setSelectedKey(courseId, parsedLessonId.toString());

                setIsLoadingContent(true);
                getHtmlContent(courseId.toString(), lessonId.toString())
                .then(html => {
                    if (html) {
                        setContentHTML(html);
                    }
                })
                .finally(() => {
                    setIsLoadingContent(false);
                });
                
            } else {
                SelectedKeyManager.clearSelectedKey(courseId);
            }
        }
        
        return () => {
            clearLessonIndex();
        };
    }, [dispatch, lessonId, courseId, lessons]);

    const findNextLesson = useCallback((currentLessonId: number) => {
        try {
            const nextId = getNextLessonId(currentLessonId);
            const url = getHtmlUrlByLessonId(nextId);
            if (url) {
                navigate(`/course/${courseId}/${nextId}`);
            } else {
                message.info('没有更多课程了');
            }
        } catch (error) {
            log.error(error);
        }
    }, [courseId]);

    useEffect(() => {
        eventBus.on(EVENTS.LOAD_NEXT_LESSON, findNextLesson);
        return () => {
            eventBus.off(EVENTS.LOAD_NEXT_LESSON, findNextLesson);
        };
    }, [findNextLesson]);

    const handleMenuClick: MenuProps['onClick'] = (info) => {
        if (!isLogin) {
            message.error('需要登录后才可以查看');
            return;
        }

        const selectedLessonId = info.key;
        if (selectedLessonId === SelectedKeyManager.getSelectedKey(courseId)) {
            return;
        }
        
        SelectedKeyManager.setSelectedKey(courseId, selectedLessonId);

        const parsedLessonId = parseInt(selectedLessonId, 10);
        const htmlUrl = getHtmlUrlByLessonId(parsedLessonId);
        if (htmlUrl) {
            setHtmlUrl(htmlUrl);
            setIsLoadingContent(true);
            log.debug(`handleMenuClick: ${htmlUrl}`);
            navigate(`/course/${courseId}/${selectedLessonId}`);
        } else {
            message.error('未找到当前课程的HTML内容');
        }
    };

    const lessonsMenuItem = useMemo(() => convertLessonsToMenuItems(lessons), [lessons]);
    const selectedKey = SelectedKeyManager.getSelectedKey(courseId) || '';

    return (
        <Spin spinning={isLoadingLessons} tip="加载课程列表...">
            <Layout className={styles.post}>
                <Sider
                    width={300}
                    className={styles.sider}
                    collapsible
                    collapsed={collapsed}
                    onCollapse={(value) => setCollapsed(value)}
                >
                    <Menu
                        key="lesson"
                        mode="inline"
                        onClick={handleMenuClick}
                        selectedKeys={[selectedKey]}
                        className={styles.sideMenu}
                        items={lessonsMenuItem}
                    />
                </Sider>
                <Layout className={styles.content}>
                    {contentHTML && <ContentWithTextPlayer contentHTML={contentHTML} />}
                    {isLoadingContent && (
                        <div className={styles.loadingContainer}>
                            <Spin tip="加载课程内容..." size="large" />
                        </div>
                    )}
                </Layout>
            </Layout>
        </Spin>
    );
};